import React from 'react'
import { Box, Message, Text } from '@workwhile/ui'
import { useColumbiaTimesheetContextValue } from '../context/ColumbiaTimesheetProvider'

type RowDiff = NonNullable<
  ReturnType<
    typeof useColumbiaTimesheetContextValue
  >['state']['diffMutationState']['data']
>['rowDiffs'][number]

type UnmatchedRow = NonNullable<
  ReturnType<
    typeof useColumbiaTimesheetContextValue
  >['state']['diffMutationState']['data']
>['unmatchedRows'][number]

const formatIssue = (issue: UnmatchedRow['issues'][number]) => {
  switch (issue) {
    case 'missing_in_workwhile':
      return 'Missing in WorkWhile'
    case 'missing_in_vndly':
      return 'Missing in VNDLY'
    default: {
      const _exhaustive: never = issue
      return _exhaustive
    }
  }
}

const RowDiffCard = ({ row }: { row: RowDiff }) => (
  <Box
    border="1px solid"
    borderColor="neutrals.100"
    borderRadius="default"
    p={3}
    mb={2}
  >
    <Text fontWeight="bold">
      Row {row.rowNumber}: {row.workerName}
    </Text>
    <Text fontSize={1} color="lightText" mt={1}>
      {row.unit} · {row.address} · {row.positionName}
    </Text>
    <Box mt={2}>
      {row.hoursChanges.map((change) => (
        <Text key={change.date} fontSize={1} mt={1}>
          {change.date}: WorkWhile {change.workwhileHours}h → VNDLY{' '}
          {change.vndlyHours}h
        </Text>
      ))}
    </Box>
  </Box>
)

const UnmatchedRowCard = ({ row }: { row: UnmatchedRow }) => (
  <Box
    border="1px solid"
    borderColor="neutrals.100"
    borderRadius="default"
    p={3}
    mb={2}
  >
    <Text fontWeight="bold">
      {row.rowNumber != null ? `Row ${row.rowNumber}: ` : ''}
      {row.workerName}
    </Text>
    <Text fontSize={1} color="lightText" mt={1}>
      {row.unit} · {row.address} · {row.positionName}
    </Text>
    <Text fontSize={1} mt={2}>
      {row.issues.map(formatIssue).join(', ')}
    </Text>
  </Box>
)

export const ColumbiaTimesheetDiffResults = () => {
  const {
    state: {
      diffMutationState: { isSuccess, data },
    },
  } = useColumbiaTimesheetContextValue()

  if (!isSuccess || !data) {
    return null
  }

  const hasRowDiffs = data.rowDiffs.length > 0
  const hasUnmatched = data.unmatchedRows.length > 0

  if (!hasRowDiffs && !hasUnmatched) {
    return (
      <Message variant="success" mt={3}>
        No differences found for {data.startDate} – {data.endDate}.
      </Message>
    )
  }

  return (
    <Box mt={4}>
      <Text fontWeight="bold" mb={2}>
        Results ({data.startDate} – {data.endDate})
      </Text>
      {hasRowDiffs ? (
        <Box mb={4}>
          <Text fontWeight="bold" mb={2}>
            Hours diffs ({data.rowDiffs.length})
          </Text>
          {data.rowDiffs.map((row) => (
            <RowDiffCard key={`${row.rowNumber}-${row.workerName}`} row={row} />
          ))}
        </Box>
      ) : null}
      {hasUnmatched ? (
        <Box>
          <Text fontWeight="bold" mb={2}>
            Unmatched rows ({data.unmatchedRows.length})
          </Text>
          {data.unmatchedRows.map((row, index) => (
            <UnmatchedRowCard
              key={`${row.rowNumber ?? 'ww'}-${row.workerName}-${index}`}
              row={row}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  )
}
